import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import {
    InspectionEngagementQuestionCommentDto,
    InspectionEngagementQuestionDto,
    InspectionEngagementQuestionOptionDto,
    InspectionEngagementStandardGroupDto,
    InspectionEngagementTopicDto,
    MyInspectionEngagementDetailDto,
    SaveInspectionEngagementQuestionCommentDto,
    SaveInspectionEngagementQuestionResponseDto
} from '../../../../model/modelDto';
import { InspectionQuestionType } from '../../../../model/modelEnums';
import { UnitOfWorkService } from '../../../services/unit-of-work/unit-of-work.service';
import { IsBusyService } from '../../../services/is-busy/is-busy.service';
import { GlobalErrorHandler } from '../../../services/global-error-handler';

const LOADING_KEY = 'phase8-assignment:load';
const SAVING_KEY = 'phase8-assignment:save';
const PRINTING_KEY = 'phase8-assignment:print';

@Component({
    selector: 'app-my-assignment-inspection',
    templateUrl: './my-assignment-inspection.component.html',
    styleUrls: ['./my-assignment-inspection.component.less'],
    standalone: false
})
export class MyAssignmentInspectionComponent implements OnInit, OnDestroy {
    engagement: MyInspectionEngagementDetailDto = null;
    engagementId: number;
    currentQuestion: InspectionEngagementQuestionDto = null;
    currentQuestionIndex = 0;
    responseText = '';
    selectedOptionIds: number[] = [];
    commentText = '';
    editingCommentId: number = null;
    errorMessage = '';
    questionType = InspectionQuestionType;
    private subscription = new Subscription();
    private responseSaveQueue: Promise<void> = Promise.resolve();
    private responseSaveVersions = new Map<number, number>();

    constructor(
        private uow: UnitOfWorkService,
        private route: ActivatedRoute,
        private router: Router,
        public isBusy: IsBusyService,
        private errorHandler: GlobalErrorHandler,
        private http: HttpClient
    ) {}

    ngOnInit() {
        this.subscription.add(this.route.queryParams.subscribe(params => {
            const id = Number(params.id);
            if (id) {
                this.engagementId = id;
                this.load(id);
            }
        }));
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    get topics(): InspectionEngagementTopicDto[] {
        return (this.engagement && this.engagement.topics || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    }

    get allQuestions(): InspectionEngagementQuestionDto[] {
        const questions: InspectionEngagementQuestionDto[] = [];
        this.topics.forEach(topic => {
            (topic.rootQuestions || []).slice().sort(this.questionOrder).forEach(q => questions.push(q));
            (topic.standards || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                .forEach(group => (group.questions || []).slice().sort(this.questionOrder).forEach(q => questions.push(q)));
        });
        return questions.filter(q => this.isQuestionVisible(q));
    }

    get canEdit() {
        return !!this.engagement && (this.engagement.isTeamCaptain || this.engagement.isCaptain || this.engagement.status !== 4);
    }

    get previousQuestion() {
        return this.allQuestions[this.currentQuestionIndex - 1] || null;
    }

    get nextQuestion() {
        return this.allQuestions[this.currentQuestionIndex + 1] || null;
    }

    get currentComments() {
        return this.currentQuestion && this.currentQuestion.comments || [];
    }

    load(id: number) {
        this.isBusy.set(LOADING_KEY, true, 'Loading assignment');
        this.uow.getMyInspectionEngagementById(id)
            .then(result => {
                const value: any = result && (result as any[]).length ? (result as any[])[0] : result;
                this.engagement = this.normaliseDetail(value);
                if (!this.engagement) {
                    this.router.navigate(['/my-assignments']);
                    return;
                }
                const first = this.allQuestions[0];
                if (first) {
                    this.selectQuestion(first);
                }
            })
            .catch(error => {
                this.errorMessage = 'Unable to load this assignment.';
                this.errorHandler.handleError(error);
            })
            .finally(() => this.isBusy.set(LOADING_KEY, false));
    }

    async printReport() {
        if (!this.engagementId || this.isBusy.isBusy) {
            return;
        }

        this.isBusy.set(PRINTING_KEY, true, 'Preparing assignment report');
        try {
            const report = await firstValueFrom(this.http.get(
                '/breeze/getInspectionEngagementReport',
                {
                    params: { id: this.engagementId },
                    responseType: 'blob'
                }));
            const reportUrl = URL.createObjectURL(report);
            window.open(reportUrl, '_blank');
            window.setTimeout(() => URL.revokeObjectURL(reportUrl), 60000);
        } catch (error) {
            this.errorMessage = 'Unable to generate the assignment report.';
            this.errorHandler.handleError(error);
        } finally {
            this.isBusy.set(PRINTING_KEY, false);
        }
    }

    selectQuestion(question: InspectionEngagementQuestionDto) {
        if (!question || !this.isQuestionVisible(question)) {
            return;
        }
        this.currentQuestion = question;
        this.currentQuestionIndex = this.allQuestions.findIndex(item => item.id === question.id);
        this.selectedOptionIds = this.getSelectedOptionIds(question);
        this.responseText = question.responseText || '';
        this.commentText = '';
        this.editingCommentId = null;
    }

    isQuestionVisible(question: InspectionEngagementQuestionDto) {
        if (!question || (!this.engagement.isTeamCaptain && question.isAssignedToMe === false)) {
            return false;
        }
        const targetIds = new Set([question.id, question.sourceQuestionId]);
        const sameTopicSnapshots = this.allSnapshotQuestions()
            .filter(candidate => candidate.topicId === question.topicId);
        const hasIncomingBranch = sameTopicSnapshots.some(candidate =>
            (candidate.options || []).some(option => option.branchingQuestionId != null && targetIds.has(option.branchingQuestionId))
        );
        if (!hasIncomingBranch) {
            return true;
        }
        return sameTopicSnapshots.some(candidate => {
            const selectedIds = new Set(this.getSelectedOptionIds(candidate));
            return (candidate.options || []).some(option =>
                selectedIds.has(option.id)
                && option.branchingQuestionId != null
                && targetIds.has(option.branchingQuestionId));
        });
    }

    toggleTopic(topic: InspectionEngagementTopicDto) {
        (topic as any).isCollapsed = !(topic as any).isCollapsed;
    }

    toggleStandard(group: InspectionEngagementStandardGroupDto) {
        (group as any).isCollapsed = !(group as any).isCollapsed;
    }

    selectOption(option: InspectionEngagementQuestionOptionDto, checked: boolean) {
        if (!this.currentQuestion || !this.canEdit) {
            return;
        }
        if (this.isMultipleChoice()) {
            const noAnswerOptionIds = new Set((this.currentQuestion.options || [])
                .filter(candidate => this.isNoAnswer(candidate))
                .map(candidate => candidate.id));
            if (checked && this.isNoAnswer(option)) {
                this.selectedOptionIds = [option.id];
            } else if (checked) {
                this.selectedOptionIds = this.selectedOptionIds
                    .filter(id => !noAnswerOptionIds.has(id))
                    .concat(option.id)
                    .filter((id, index, values) => values.indexOf(id) === index);
            } else {
                this.selectedOptionIds = this.selectedOptionIds.filter(id => id !== option.id);
            }
        } else {
            this.selectedOptionIds = [option.id];
        }
        this.currentQuestion.selectedOptionId = this.selectedOptionIds[0] || null;
        this.currentQuestion.selectedOptionIds = [...this.selectedOptionIds];
        (this.currentQuestion.options || []).forEach(candidate => {
            candidate.isSelected = this.selectedOptionIds.indexOf(candidate.id) >= 0;
        });
        this.saveResponse(this.isNoAnswer(option));
    }

    saveResponse(markComplete: boolean) {
        if (!this.currentQuestion || !this.canEdit) {
            return;
        }
        const dto: SaveInspectionEngagementQuestionResponseDto = {
            questionId: this.currentQuestion.id,
            optionId: this.currentQuestion.selectedOptionId,
            selectedOptionIds: this.isMultipleChoice() ? this.selectedOptionIds : undefined,
            responseText: this.responseText || null,
            isComplete: markComplete,
            completedDate: markComplete ? new Date() : null,
            replaceExisting: false
        };
        const saveVersion = (this.responseSaveVersions.get(dto.questionId) || 0) + 1;
        this.responseSaveVersions.set(dto.questionId, saveVersion);
        this.responseSaveQueue = this.responseSaveQueue
            .catch(() => undefined)
            .then(() => {
                this.isBusy.set(SAVING_KEY, true, 'Saving your response');
                let saveAllowed = true;
                return this.uow.checkInspectionEngagementQuestionForExistingResponse(dto)
                    .then(result => {
                        const exists = !!(result && (result as any[]).length ? (result as any[])[0] : result);
                        if (exists && !this.engagement.isTeamCaptain) {
                            this.errorMessage = 'This question has already been answered by another team member. Only the team captain can replace it.';
                            saveAllowed = false;
                            return null;
                        }
                        if (exists && !window.confirm('This question has an existing response. Continue saving and replace it?')) {
                            saveAllowed = false;
                            return null;
                        }
                        dto.replaceExisting = exists;
                        return this.uow.updateInspectionEngagementQuestionResponse(dto);
                    })
                    .then(result => {
                        if (!saveAllowed || saveVersion !== this.responseSaveVersions.get(dto.questionId)) {
                            return;
                        }
                        const saved: any = result && (result as any[]).length ? (result as any[])[0] : result;
                        if (saved) {
                            this.mergeQuestion(saved, dto.questionId);
                        } else {
                            const question = this.allSnapshotQuestions()
                                .find(candidate => candidate.id === dto.questionId);
                            if (question) {
                                question.hasResponse = (dto.selectedOptionIds || []).length > 0 || !!dto.responseText;
                                question.isComplete = markComplete;
                            }
                        }
                    })
                    .catch(error => this.errorHandler.handleError(error))
                    .finally(() => this.isBusy.set(SAVING_KEY, false));
            });
    }

    saveCurrentAsComplete() {
        this.saveResponse(true);
    }

    clearResponse() {
        if (!this.currentQuestion || !this.canEdit) {
            return;
        }
        this.selectedOptionIds = [];
        this.responseText = '';
        this.currentQuestion.selectedOptionId = null;
        this.currentQuestion.selectedOptionIds = [];
        (this.currentQuestion.options || []).forEach(option => option.isSelected = false);
        this.currentQuestion.responseText = null;
        this.saveResponse(false);
    }

    saveComment() {
        if (!this.currentQuestion || !this.commentText.trim() || !this.canEdit) {
            return;
        }
        const dto: SaveInspectionEngagementQuestionCommentDto = {
            questionId: this.currentQuestion.id,
            text: this.commentText.trim()
        };
        this.isBusy.set(SAVING_KEY, true, 'Saving comment');
        this.uow.saveInspectionEngagementQuestionComment(dto)
            .then(result => {
                const comment: any = result && (result as any[]).length ? (result as any[])[0] : result;
                if (comment) {
                    this.currentQuestion.comments = (this.currentQuestion.comments || []).concat(comment);
                }
                this.commentText = '';
                this.editingCommentId = null;
            })
            .catch(error => this.errorHandler.handleError(error))
            .finally(() => this.isBusy.set(SAVING_KEY, false));
    }

    editComment(comment: InspectionEngagementQuestionCommentDto) {
        this.editingCommentId = comment.id;
        this.commentText = comment.text;
    }

    goPrevious() {
        if (this.previousQuestion) {
            this.selectQuestion(this.previousQuestion);
        }
    }

    goNext() {
        if (this.nextQuestion) {
            this.selectQuestion(this.nextQuestion);
        }
    }

    questionNumber(question: InspectionEngagementQuestionDto) {
        return this.allQuestions.findIndex(item => item.id === question.id) + 1;
    }

    isMultipleChoice() {
        return Number(this.currentQuestion && this.currentQuestion.questionType) === InspectionQuestionType.MultipleSelect;
    }

    isSelected(option: InspectionEngagementQuestionOptionDto) {
        return this.selectedOptionIds.indexOf(option.id) >= 0;
    }

    isNoAnswer(option: InspectionEngagementQuestionOptionDto) {
        return option.isNoAnswerNeeded === true || (option.optionText || '').toLowerCase() === 'no answer needed';
    }

    private mergeQuestion(saved: any, questionId: number) {
        const question = this.allSnapshotQuestions()
            .find(candidate => candidate.id === questionId);
        if (!question) {
            return;
        }
        const selectedOptionIds = this.getSelectedOptionIds(saved);
        question.isComplete = saved.isComplete != null ? saved.isComplete : question.isComplete;
        question.hasResponse = saved.hasResponse != null
            ? saved.hasResponse
            : selectedOptionIds.length > 0 || !!saved.responseText;
        question.selectedOptionId = Object.prototype.hasOwnProperty.call(saved, 'selectedOptionId')
            ? saved.selectedOptionId
            : question.selectedOptionId;
        question.selectedOptionIds = [...selectedOptionIds];
        (question.options || []).forEach(option => {
            option.isSelected = selectedOptionIds.indexOf(option.id) >= 0;
        });
        question.responseText = saved.responseText != null ? saved.responseText : question.responseText;
        question.responseDate = saved.responseDate || new Date();
        if (saved.comments) {
            question.comments = saved.comments;
        }
        if (this.currentQuestion && this.currentQuestion.id === questionId) {
            this.selectedOptionIds = selectedOptionIds;
            this.responseText = question.responseText || '';
        }
    }

    private hasResponse() {
        return this.selectedOptionIds.length > 0 || !!this.responseText;
    }

    private getSelectedOptionIds(question: InspectionEngagementQuestionDto) {
        if (question && question.selectedOptionIds && question.selectedOptionIds.length) {
            return [...question.selectedOptionIds];
        }
        const selectedFromOptions = (question && question.options || [])
            .filter(option => option.isSelected)
            .map(option => option.id);
        if (selectedFromOptions.length) {
            return selectedFromOptions;
        }
        return question && question.selectedOptionId != null ? [question.selectedOptionId] : [];
    }

    private allSnapshotQuestions() {
        const result: InspectionEngagementQuestionDto[] = [];
        (this.engagement && this.engagement.topics || []).forEach(topic => {
            result.push(...(topic.rootQuestions || []));
            (topic.standards || []).forEach(group => result.push(...(group.questions || [])));
        });
        return result;
    }

    private questionOrder(a: InspectionEngagementQuestionDto, b: InspectionEngagementQuestionDto) {
        return (a.displayOrder || 0) - (b.displayOrder || 0);
    }

    private normaliseDetail(value: any): MyInspectionEngagementDetailDto {
        if (!value) {
            return null;
        }
        value.topics = value.topics || value.topicGroups || [];
        value.isTeamCaptain = value.isTeamCaptain != null ? value.isTeamCaptain : value.isCaptain;
        value.code = value.code || value.engagementCode;
        value.teamCaptain = value.teamCaptain || value.inspectionLeadName;
        value.topics.forEach((topic: any) => {
            topic.rootQuestions = topic.rootQuestions || topic.questions || [];
            topic.standards = topic.standards || topic.standardGroups || [];
            topic.rootQuestions.forEach(q => this.normaliseQuestion(q, topic.topicId));
            topic.standards.forEach(group => {
                group.questions = group.questions || [];
                group.questions.forEach(q => this.normaliseQuestion(q, topic.topicId, group.standardId));
            });
        });
        return value as MyInspectionEngagementDetailDto;
    }

    private normaliseQuestion(question: any, topicId: number, standardId: number = null) {
        question.topicId = question.topicId || topicId;
        question.standardId = question.standardId == null ? standardId : question.standardId;
        question.sourceQuestionId = question.sourceQuestionId || question.inspectionQuestionId || question.questionId;
        question.options = question.options || question.inspectionEngagementQuestionOptions || [];
        question.comments = question.comments || question.inspectionEngagementQuestionComments || [];
        question.comments.forEach(comment => {
            comment.authorName = comment.authorName || comment.inspectionEngagementTeamMemberName;
        });
        question.hasResponse = question.hasResponse != null
            ? question.hasResponse
            : question.selectedOptionId != null || !!question.responseText || !!question.responseDate;
        question.questionText = question.questionText || question.text || question.name || '';
        question.questionCode = question.questionCode || question.code || '';
        question.helpText = question.helpText || null;
    }
}
